/**
 * BulkActionBar.tsx
 *
 * Floating Action Toolbar appearing when multiple tasks are selected.
 * Provides batch status transition, priority change, tag management, date shifting, and archiving.
 */

import React from 'react';
import { globalTaskFlowStore } from '../../../plugins/taskflow/engine/taskflow-store.js';

export interface BulkActionBarProps {
  selectedIds: string[];
  workspaceId: string;
  onClearSelection: () => void;
  onRefresh?: () => void;
}

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedIds,
  workspaceId,
  onClearSelection,
  onRefresh,
}) => {
  if (selectedIds.length === 0) return null;

  const handleStatusChange = (status: string) => {
    globalTaskFlowStore.applyBulkUpdate(selectedIds, { status }, 'user');
    onRefresh?.();
  };

  const handlePriorityChange = (priority: any) => {
    globalTaskFlowStore.applyBulkUpdate(selectedIds, { priority }, 'user');
    onRefresh?.();
  };

  const handleDateShift = (days: number) => {
    globalTaskFlowStore.applyBulkDateShift(selectedIds, days, 'user');
    onRefresh?.();
  };

  const handleArchive = () => {
    globalTaskFlowStore.applyBulkArchive(selectedIds, 'user');
    onClearSelection();
    onRefresh?.();
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'var(--theme-bg-modal, rgba(15, 23, 42, 0.95))',
        border: '1px solid var(--theme-accent, #6366f1)',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.6), 0 0 15px rgba(99, 102, 241, 0.25)',
        borderRadius: '30px',
        padding: '8px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        zIndex: 90,
        backdropFilter: 'blur(16px)',
        color: 'var(--theme-text-primary, #f8fafc)',
        fontSize: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}>
        <span
          style={{
            backgroundColor: 'var(--theme-accent, #6366f1)',
            color: '#fff',
            padding: '2px 7px',
            borderRadius: '12px',
            fontSize: '11px',
          }}
        >
          {selectedIds.length}
        </span>
        <span>Selected</span>
      </div>

      <div style={{ width: '1px', height: '18px', backgroundColor: 'var(--theme-border, rgba(255, 255, 255, 0.15))' }} />

      {/* Status Picker */}
      <select
        defaultValue=""
        onChange={(e) => {
          if (e.target.value) {
            handleStatusChange(e.target.value);
            e.target.value = '';
          }
        }}
        style={{
          backgroundColor: 'var(--theme-bg-input, rgba(0, 0, 0, 0.4))',
          color: 'var(--theme-text-primary, #fff)',
          border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
          borderRadius: '6px',
          padding: '4px 8px',
          fontSize: '11px',
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        <option value="" disabled>Status ➔</option>
        <option value="backlog">Backlog</option>
        <option value="in-progress">In Progress</option>
        <option value="blocked">Blocked</option>
        <option value="review">Review</option>
        <option value="done">Done</option>
      </select>

      {/* Priority Picker */}
      <select
        defaultValue=""
        onChange={(e) => {
          if (e.target.value) {
            handlePriorityChange(e.target.value);
            e.target.value = '';
          }
        }}
        style={{
          backgroundColor: 'var(--theme-bg-input, rgba(0, 0, 0, 0.4))',
          color: 'var(--theme-text-primary, #fff)',
          border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
          borderRadius: '6px',
          padding: '4px 8px',
          fontSize: '11px',
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        <option value="" disabled>Priority ➔</option>
        <option value="low">Low</option>
        <option value="normal">Normal</option>
        <option value="medium">Medium</option>
        <option value="high">High</option>
        <option value="urgent">Urgent 🔥</option>
      </select>

      {/* Date Shift Buttons */}
      <div style={{ display: 'flex', gap: '4px' }}>
        <button
          onClick={() => handleDateShift(1)}
          title="Postpone +1 Day"
          style={{
            backgroundColor: 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.08))',
            color: 'var(--theme-text-secondary, #cbd5e1)',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '11px',
            cursor: 'pointer',
          }}
        >
          +1d
        </button>
        <button
          onClick={() => handleDateShift(7)}
          title="Postpone +1 Week"
          style={{
            backgroundColor: 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.08))',
            color: 'var(--theme-text-secondary, #cbd5e1)',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '11px',
            cursor: 'pointer',
          }}
        >
          +1w
        </button>
      </div>

      <div style={{ width: '1px', height: '18px', backgroundColor: 'var(--theme-border, rgba(255, 255, 255, 0.15))' }} />

      {/* Archive Button */}
      <button
        onClick={handleArchive}
        style={{
          backgroundColor: 'var(--theme-status-danger-bg, rgba(239, 68, 68, 0.2))',
          color: 'var(--theme-status-danger-text, #f87171)',
          border: 'none',
          borderRadius: '6px',
          padding: '4px 10px',
          fontSize: '11px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        📥 Archive
      </button>

      {/* Deselect / Dismiss */}
      <button
        onClick={onClearSelection}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--theme-text-muted, #94a3b8)',
          cursor: 'pointer',
          fontSize: '14px',
          padding: '0 4px',
        }}
      >
        ✕
      </button>
    </div>
  );
};
